import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import { FilterSelected } from "@lhd/components/SearchAreaBox/types";
import { ROUTES } from "@lhd/constants";
import { useGetAuth, useGetPermissionsEachPage } from "@lhd/hooks";
import { IPermissionItem, Worker } from "@lhd/types";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSlicerFilterState } from "@lhd/components/SearchAreaBox/hooks/useSlicerFilterState";
import {
  BUSINESS_TRIP_REPORTS_STATUS,
  findIsApplicant,
} from "../BusinessTripReportEdit/hook";
export interface RegularValue {
  app_part_id: number;
  id: number;
  value: string | number | (string | number)[] | null;
  background_color: string | null;
  text_color: string | null;
}

export interface BusinessTripReportInfo {
  status: string;
  status_label: string;
  status_field_id: number;
  can_edit_form: boolean;
  available_statuses: string[];
}

export interface BusinessTripReportRecord {
  id: number;
  app_id: number;
  user_id: number;
  user_update_id: number | null;
  row_id: number;
  parent_record_id: number | null;
  record_type: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  regular_values: RegularValue[];
  business_trip_expense_applicant: number[];
  attachments: any[];
  is_used_referenced_data: boolean;
  last_update_at: string;
  clone_value_hash: string | null;
  share_value_hash: string[];
  business_trip_report_info: BusinessTripReportInfo;
  current_user_is_manager?: boolean;
}

const appID = "758";

const useList = () => {
  const navigate = useNavigate();
  const { getCurrentWorker, auth } = useGetAuth();

  const currentWorker = getCurrentWorker();

  const permissionsEachPage = useGetPermissionsEachPage();
  const permission = permissionsEachPage[
    ROUTES.BUSINESS_TRIP_REPORTS
  ] as IPermissionItem;
  const [searchParams, setSearchParams] = useSearchParams();

  const viewType = searchParams.get("view_type") || "owner";
  const isOwner = viewType === "owner";

  const [statusFilterSelected, setStatusFilterSelected] = useSlicerFilterState<
    FilterSelected[]
  >("status", []);
  const [categoryFilterSelected, setCategoryFilterSelected] =
    useSlicerFilterState<FilterSelected[]>("category", []);

  const [closeSignal, setCloseSignal] = useState(0);

  const handleChangeStatus = (val: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    setCloseSignal((prev) => prev + 1);
  };

  const handleChangeCategory = (val: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    setCloseSignal((prev) => prev + 1);
  };

  const checkedStatus = statusFilterSelected.filter(
    (x) => x.status === "CHECKED" || x.status === "SELECTED",
  );
  const isStatusAllChecked =
    statusFilterSelected.length > 0 &&
    checkedStatus.length === statusFilterSelected.length;

  const checkedCategory = categoryFilterSelected.filter(
    (x) => x.status === "CHECKED" || x.status === "SELECTED",
  );
  const isCategoryAllChecked =
    categoryFilterSelected.length > 0 &&
    checkedCategory.length === categoryFilterSelected.length;

  const additionalFilter = {
    view_type: viewType,
    ...(isStatusAllChecked
      ? {}
      : {
          filter_status: checkedStatus.map((x) => x.value),
        }),
    ...(isCategoryAllChecked
      ? {}
      : {
          filter_category: checkedCategory.map((x) => x.value),
        }),
  };

  useEffect(() => {
    if (!auth?.can_approve_business_trip_report && !isOwner) {
      navigate(ROUTES.NOT_FOUND_PAGE);
    }
  }, [auth?.can_approve_business_trip_report, isOwner]);

  return {
    appID,
    appType: APP_TYPES.BUSINESS_TRIP_REPORTS,
    url: ROUTES.BUSINESS_TRIP_REPORTS,
    permission,
    hasQuickView: true,
    statusFilterSelected,
    setStatusFilterSelected,
    categoryFilterSelected,
    setCategoryFilterSelected,
    statusKeys: ["status"],
    categoryKeys: ["category"],
    handleChangeStatus,
    handleChangeCategory,
    closeSignal,
    setCloseSignal,
    additionalFilter,
    isOwner,
    configShowBtnsOnHeader: {
      showDelete: isOwner,
      showShare: false,
      showAddNew: !!isOwner,
      showUndoRedoButtons: true,
      showViewModeSwitch: true,
      hideAddNewRecordButton: true,
    },
    configActionBtnsOnRow: {
      reorder: true,
    },
    configActionDisabledColumns: {
      checkbox: (record: any, appParts: any) => {
        if (!record) return false;

        const isApplicant = findIsApplicant(
          record,
          currentWorker as Worker,
          appParts,
        );

        if (
          record?.business_trip_report_info?.status ===
            BUSINESS_TRIP_REPORTS_STATUS.DRAFTING &&
          (record?.user_id === auth?.id || isApplicant)
        ) {
          return false;
        }
        return true;
      },
    },
    showColumnAction: true,
    isRecordEditable: (record: any) => {
      if (!isOwner) return false;
      const info = record?.itemRecord?.business_trip_report_info;
      return !!info?.can_edit_form || record?.isNew;
    },
    isRecordDeletable: (record: any) => {
      if (!isOwner) return false;
      const isManager =
        permissionsEachPage[ROUTES.BUSINESS_TRIP_REPORTS]?.delete;
      const info = record?.itemRecord?.business_trip_report_info;
      const isDrafting = info?.status === "drafting";
      return !!(isManager || isDrafting);
    },
  };
};

export type Props = ReturnType<typeof useList>;

export default useList;
